import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";

import { Modal, Text } from "@fragments";
import api from "@helpers/api";

import Player from "@pages/Content/components/Player";

/**
 * The content player in a modal. The welcome video is a regular content, so
 * the modal fetches it like the Content page does and the player keeps every
 * feature (HLS, captions, progress, watched at 80%). It always starts from
 * zero: the card's muted preview is what the member just saw, not a lesson
 * they are resuming. Closing unmounts the player, which tears the media
 * pipeline down.
 */
export default function PlayerModal({ open, contentId, onClose }) {
    const { t } = useTranslation("community");

    const [content, setContent] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!open || !contentId) return undefined;

        let cancelled = false;
        setContent(null);
        setFailed(false);

        (async () => {
            try {
                const { data } = await api.get(`content/${contentId}`);
                if (cancelled) return;
                if (data?.id) setContent(data);
                else setFailed(true);
            } catch (error) {
                console.log(error);
                if (!cancelled) setFailed(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, contentId]);

    const vertical = content?.video?.aspect_ratio < 1;

    return (
        <Modal
            open={open}
            onClose={onClose}
            variant="card"
            size={vertical ? 520 : 960}
            className={`welcome-video-modal${vertical ? " is-vertical" : ""}`}
            closeLabel={t("welcomeVideo.close")}
        >
            <div className="welcome-video-modal__stage">
                {content && (
                    <Player
                        info={content}
                        key={content.id}
                        mode={vertical ? "vertical" : "horizontal"}
                        embedded
                        resume={false}
                    />
                )}

                {!content && !failed && (
                    <Skeleton variant="rectangular" animation="wave" className="welcome-video-modal__skeleton" />
                )}

                {failed && (
                    <div className="welcome-video-modal__error">
                        <Text.p contrast={6}>{t("welcomeVideo.loadError")}</Text.p>
                    </div>
                )}
            </div>
        </Modal>
    );
}
